/**
 * API client for Persons (PROJ-009 Phase 2)
 * Uses /api/v1/persons endpoints (direct, no /parties prefix)
 */

import { apiRequest } from './standard-api';
import type { Person } from '@/types/parties';

interface PersonListResponse {
  data: Person[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface PersonListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * List persons with optional filtering and pagination
 * Used for lookup/search in incident forms
 */
export async function listPersons(params: PersonListParams = {}): Promise<PersonListResponse> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.search) queryParams.set('search', params.search);
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  const response = await apiRequest<Person[]>(
    `/api/v1/persons${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load persons');
  }

  // Return both data and meta for pagination
  return {
    data: response.data,
    meta: {
      page: (response.meta as any).page || 1,
      limit: (response.meta as any).limit || 1000,
      total: (response.meta as any).total || response.data.length,
      totalPages: (response.meta as any).pages || 1,
      hasMore: (response.meta as any).has_next_page || false,
    },
  };
}

/**
 * Get person by ID
 */
export async function getPerson(id: string): Promise<Person> {
  const response = await apiRequest<Person>(
    `/api/v1/persons/${id}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load person');
  }

  return response.data;
}

/**
 * Search persons by name or email (for autocomplete)
 */
export async function searchPersons(query: string, limit: number = 10): Promise<Person[]> {
  const response = await listPersons({
    search: query,
    limit,
    sortBy: 'fullName',
    sortOrder: 'asc'
  });

  return response.data;
}

/**
 * Create new person
 */
export async function createPerson(data: Partial<Person>): Promise<Person> {
  const response = await apiRequest<Person>(
    `/api/v1/persons`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.data) {
    throw new Error('Failed to create person');
  }

  return response.data;
}

/**
 * Update person by ID
 */
export async function updatePerson(id: string, data: Partial<Person>): Promise<Person> {
  const response = await apiRequest<Person>(
    `/api/v1/persons/${id}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );

  if (!response.data) {
    throw new Error('Failed to update person');
  }

  return response.data;
}

/**
 * Delete person by ID (soft delete)
 */
export async function deletePerson(id: string): Promise<void> {
  await apiRequest<void>(
    `/api/v1/persons/${id}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

/**
 * Archive person
 * @param personId - Person ID to archive
 * @param reason - Optional reason for archiving
 * @returns Updated person record
 */
export async function archivePerson(personId: string, reason?: string): Promise<Person> {
  const response = await apiRequest<Person>(
    `/api/v1/persons/${personId}/archive`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );

  if (!response.data) {
    throw new Error('Failed to archive person');
  }

  return response.data;
}

/**
 * Unarchive person
 * @param personId - Person ID to unarchive
 * @returns Updated person record
 */
export async function unarchivePerson(personId: string): Promise<Person> {
  const response = await apiRequest<Person>(
    `/api/v1/persons/${personId}/unarchive`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to unarchive person');
  }

  return response.data;
}

/**
 * PROJ-038: Bulk delete response interface
 */
export interface BulkDeleteResponse {
  deleted: number;
  failed: string[];
  total: number;
}

/**
 * PROJ-038: Bulk delete persons
 * @param ids - Array of person IDs to delete
 * @returns Summary of bulk delete operation
 */
export async function bulkDeletePersons(ids: string[]): Promise<BulkDeleteResponse> {
  const response = await apiRequest<BulkDeleteResponse>(
    `/api/v1/persons/bulk`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }
  );

  if (!response.data) {
    throw new Error('Failed to bulk delete persons');
  }

  return response.data;
}
